import { rest } from 'msw'

import { config } from '@percent/admin-dashboard/config'
import { mockPartnerDetail, mockPartnerList } from '@percent/admin-dashboard/fixtures/api/partner/mockPartnerDetail'
import { mockPartnerRoles } from '@percent/admin-dashboard/fixtures/api/partner/mockPartnerRoles'

const API = config.urls.api

const makePartnerConfigRecord = (partnerId: string | readonly string[], key: string, value: unknown) => ({
  data: { id: `cfg-${key}`, partnerId: [partnerId].flat()[0], key, value, createdAt: '2025-01-01T00:00:00Z' }
})

export const partnerHandlers = [
  rest.get(`${API}v1/admin/partners/:id`, async (_, res, ctx) => {
    return res(ctx.status(200), ctx.json(mockPartnerDetail))
  }),
  rest.get(`${API}v1/admin/partners`, async (_, res, ctx) => {
    return res(ctx.status(200), ctx.json(mockPartnerList))
  }),
  rest.get(`${API}v1/admin/iam/partner/:partnerId/roles`, async (_, res, ctx) => {
    return res(ctx.status(200), ctx.json(mockPartnerRoles))
  }),
  rest.get(`${API}v1/admin/partners/:partnerId/giving/config/features`, (req, res, ctx) =>
    res(
      ctx.status(200),
      ctx.json(
        makePartnerConfigRecord(req.params.partnerId, 'features', {
          profileSecondaryEmails: 'enabled',
          opportunityTimeLogsRewardsEligibility: 'disabled'
        })
      )
    )
  ),
  rest.get(`${API}v1/admin/partners/:partnerId/giving/config/transactional_emails`, (req, res, ctx) =>
    res(
      ctx.status(200),
      ctx.json(makePartnerConfigRecord(req.params.partnerId, 'transactional_emails', { welcomeEmail: 'disabled' }))
    )
  ),
  rest.get(`${API}v1/admin/partners/:partnerId/donation/config/gift_note`, (req, res, ctx) =>
    res(ctx.status(200), ctx.json(makePartnerConfigRecord(req.params.partnerId, 'gift_note', { enabled: true })))
  ),
  rest.get(`${API}v1/admin/partners/:partnerId/donation/config/transactional_emails`, (req, res, ctx) =>
    res(
      ctx.status(200),
      ctx.json(makePartnerConfigRecord(req.params.partnerId, 'transactional_emails', { walletFundsEmail: 'enabled' }))
    )
  ),
  rest.get(`${API}v1/admin/partners/:partnerId/fundraising/config/public_fundraisers`, (req, res, ctx) =>
    res(
      ctx.status(200),
      ctx.json(makePartnerConfigRecord(req.params.partnerId, 'public_fundraisers', { enabled: false }))
    )
  ),
  rest.get(`${API}v1/admin/partners/:partnerId/donation-matching/config/familial_donation_matching`, (req, res, ctx) =>
    res(
      ctx.status(200),
      ctx.json(makePartnerConfigRecord(req.params.partnerId, 'familial_donation_matching', { enabled: true }))
    )
  ),
  rest.get(`${API}v1/admin/partners/:partnerId/donation-matching/config/donation_date_eligibility`, (req, res, ctx) =>
    res(
      ctx.status(200),
      ctx.json(
        makePartnerConfigRecord(req.params.partnerId, 'donation_date_eligibility', {
          enabled: false,
          source: { type: 'startDate' }
        })
      )
    )
  ),
  rest.get(
    `${API}v1/admin/partners/:partnerId/donation-matching/config/direct_to_campaign_match_requests`,
    (req, res, ctx) =>
      res(
        ctx.status(200),
        ctx.json(makePartnerConfigRecord(req.params.partnerId, 'direct_to_campaign_match_requests', { enabled: false }))
      )
  ),
  rest.get(`${API}v1/admin/partners/:partnerId/giving/config/theme`, (req, res, ctx) =>
    res(
      ctx.status(200),
      ctx.json(
        makePartnerConfigRecord(req.params.partnerId, 'theme', {
          colours: {
            primary: '#3B5BDB',
            primaryHover: '#364FC7',
            primaryActive: '#2F44B0',
            primaryFocus: '#3B5BDB',
            primary100: '#E7EEFF',
            primary400: '#748FFC',
            primary600: '#4263EB',
            info30: '#E8F4FD',
            info100: '#C5DFF8',
            info400: '#74B5F5'
          },
          overlayGradientPercentage: 60
        })
      )
    )
  ),
  rest.get(`${API}v1/admin/partners/:partnerId/giving/config/supported_currencies`, (req, res, ctx) =>
    res(
      ctx.status(200),
      ctx.json(makePartnerConfigRecord(req.params.partnerId, 'supported_currencies', ['GBP', 'USD', 'EUR']))
    )
  ),
  rest.get(`${API}v1/admin/partners/:partnerId/giving/config/home_page_banner_content`, (req, res, ctx) =>
    res(
      ctx.status(200),
      ctx.json(
        makePartnerConfigRecord(req.params.partnerId, 'home_page_banner_content', {
          welcomeCopy: true,
          searchBar: true,
          welcomeCopyTitle: 'Welcome to your giving platform',
          welcomeCopySubtitle: 'Make a difference today',
          welcomeCopyColor: '#ffffff'
        })
      )
    )
  ),
  rest.get(`${API}v1/admin/partners/:partnerId/giving/config/regions`, (req, res, ctx) =>
    res(
      ctx.status(200),
      ctx.json(
        makePartnerConfigRecord(req.params.partnerId, 'regions', {
          europe: { id: 'europe', name: 'Europe', countryCodes: ['GBR', 'DEU', 'FRA', 'ESP', 'ITA'] },
          americas: { id: 'americas', name: 'Americas', countryCodes: ['USA', 'CAN', 'BRA'] }
        })
      )
    )
  ),
  rest.get(`${API}v1/admin/partners/:partnerId/giving/config/calendar_integration`, (req, res, ctx) =>
    res(
      ctx.status(200),
      ctx.json(makePartnerConfigRecord(req.params.partnerId, 'calendar_integration', ['google', 'outlook']))
    )
  ),
  rest.get(`${API}v1/admin/partners/:partnerId/giving/config/help_link`, (req, res, ctx) =>
    res(
      ctx.status(200),
      ctx.json(
        makePartnerConfigRecord(req.params.partnerId, 'help_link', {
          link: 'https://help.example.com',
          title: 'Help Centre'
        })
      )
    )
  ),
  rest.get(`${API}v1/admin/partners/:partnerId/giving/config/preferred_countries`, (req, res, ctx) =>
    res(
      ctx.status(200),
      ctx.json(makePartnerConfigRecord(req.params.partnerId, 'preferred_countries', ['GBR', 'USA', 'AUS']))
    )
  ),
  rest.get(`${API}v1/admin/partners/:partnerId/giving/config/require_organisation_time_log`, (req, res, ctx) =>
    res(
      ctx.status(200),
      ctx.json(makePartnerConfigRecord(req.params.partnerId, 'require_organisation_time_log', false))
    )
  ),
  rest.get(`${API}v1/admin/partners/:partnerId/giving/config/spotlighted_orgs`, (req, res, ctx) =>
    res(
      ctx.status(200),
      ctx.json(
        makePartnerConfigRecord(req.params.partnerId, 'spotlighted_orgs', {
          organisationIds: ['org_abc123'],
          partnerLogo: 'https://example.com/logo.png'
        })
      )
    )
  ),
  rest.get(`${API}v1/admin/partners/:partnerId/giving/config/featured`, (req, res, ctx) =>
    res(
      ctx.status(200),
      ctx.json(
        makePartnerConfigRecord(req.params.partnerId, 'featured', [
          {
            kind: 'featured_organisation',
            organisationId: 'org_xyz',
            title: 'Example Org',
            description: 'A great cause'
          }
        ])
      )
    )
  ),
  rest.get(`${API}v1/admin/partners/:partnerId/donation/config/restricted_countries_for_donation`, (req, res, ctx) =>
    res(
      ctx.status(200),
      ctx.json(
        makePartnerConfigRecord(req.params.partnerId, 'restricted_countries_for_donation', {
          blockedRegions: [{ countryCode: 'RUS' }]
        })
      )
    )
  ),
  rest.get(`${API}v1/admin/partners/:partnerId/giving/config/date_period_configuration`, (req, res, ctx) =>
    res(
      ctx.status(200),
      ctx.json(
        makePartnerConfigRecord(req.params.partnerId, 'date_period_configuration', {
          date: '2026-02-01',
          period: 'year'
        })
      )
    )
  ),
  rest.get(`${API}v1/admin/partners/:partnerId/volunteering/config/volunteering_allowance`, (req, res, ctx) =>
    res(
      ctx.status(200),
      ctx.json(
        makePartnerConfigRecord(req.params.partnerId, 'volunteering_allowance', {
          renewsAt: '2026-01-01',
          allowanceMinutes: 1200,
          periodicity: 'annual'
        })
      )
    )
  ),
  rest.get(`${API}v1/admin/partners/:partnerId/gamification/config/achievement_reset_date`, (req, res, ctx) =>
    res(
      ctx.status(200),
      ctx.json(makePartnerConfigRecord(req.params.partnerId, 'achievement_reset_date', '2026-03-15T09:30:00.000Z'))
    )
  ),
  rest.put(`${API}v1/admin/partners/:partnerId/giving/config`, (_, res, ctx) => res(ctx.status(200))),
  rest.put(`${API}v1/admin/partners/:partnerId/donation/config`, (_, res, ctx) => res(ctx.status(200))),
  rest.put(`${API}v1/admin/partners/:partnerId/fundraising/config`, (_, res, ctx) => res(ctx.status(200))),
  rest.put(`${API}v1/admin/partners/:partnerId/donation-matching/config`, (_, res, ctx) => res(ctx.status(200)))
]
